import axios, { API_BASE } from './axios-config'
import type { SnowflakeId } from '@/types/snowflake'
import { mapIncomingCall } from '@/lib/callPayload'

export type CallStatus = 'ringing' | 'answered' | 'missed' | 'completed' | 'voicemail' | 'ai_handled' | 'forwarded'
// How a call was dispatched when no individual staff member answered it. Null for
// an internal ring (answered by staff, never routed, or simply missed) — and also
// for voicemail, which is a terminal status rather than a dispatch destination.
export type CallDispatchType = 'ai_handoff' | 'forward'
export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

export interface TwilioTokenResponse {
  token: string
  expiresAt: string
}

export interface IncomingCall {
  callSid: string
  callerNumber: string
  callLogId: SnowflakeId
  guestId?: SnowflakeId | null
  guestName?: string | null
  reservationId?: SnowflakeId | null
  listingName?: string | null
  checkIn?: string | null
  checkOut?: string | null
  status: 'ringing'
  claimedBy?: string | null
  // Full name of the staff who claimed the call (from the call_claimed event's
  // answered_by_name). Preferred over claimedBy (a raw user id) for display.
  claimedByName?: string | null
}

export interface ActiveCall {
  callSid: string
  callerName: string
  callerNumber: string
  answeredAt: string
  answeredBy: string
  onHold: boolean
}

export interface CallLog {
  id: string
  callSid: string
  callerNumber: string
  guestName?: string | null
  listingName?: string | null
  // Same guest/property/reservation context shown live on the incoming-call
  // popup (IncomingCall below), resolved server-side from reservation_id/guest_id
  // so it's still available after the call ends.
  reservationId?: SnowflakeId | null
  otaConfirmationCode?: string | null
  checkIn?: string | null
  checkOut?: string | null
  answeredBy?: string | null
  // Full name of the staff who answered, resolved server-side. Prefer this over
  // answeredBy (a raw Snowflake id) for display.
  answeredByName?: string | null
  duration?: number | null
  status: CallStatus
  // Only field that distinguishes "the AI took it" / "it was forwarded" from
  // "nobody took it": answeredBy is null for all three.
  dispatchType?: CallDispatchType | null
  /**
   * E.164 number this call was actually forwarded to, captured server-side at
   * dispatch time. Only ever set when dispatchType === 'forward'; null for a call
   * that predates the column, which is why the UI still needs the generic
   * "External number" fallback rather than assuming this is always present.
   */
  forwardToNumber?: string | null
  isResolved: boolean
  createdAt: string
  aiSummary?: string | null
  transcript?: string | null
  recordingUrl?: string | null
  onHold: boolean
  // ─── AI post-call analysis ──────────────────────────────────────────────────
  // All null/empty until the call has been analysed. There is deliberately no
  // "analysedAt" field: professionalismScore === null IS the not-yet-analysed flag.
  /** 1-5. 5 = warm/efficient/fully addressed, 3 = correct but mechanical, 1 = rude. */
  professionalismScore?: number | null
  /** One sentence explaining the score. */
  professionalismNote?: string | null
  /** resolved | unresolved | unclear | not_applicable */
  resolutionStatus?: CallResolutionStatus | null
  /**
   * 0-3 topic codes from a fixed 12-value taxonomy. ALWAYS an array — the server
   * defaults it to [] rather than null precisely so consumers can map over it
   * unconditionally.
   */
  topics: CallTopicCode[]
}

/** The 12 bounded topic codes. Mirrors main-api's chk_call_log_topic_code and
 *  app/shared/constants/call_topics.py — the DB rejects anything outside this set. */
export type CallTopicCode =
  | 'STAY' | 'FACILITY' | 'LOCAL' | 'COMMUNITY' | 'POLICY' | 'BOOKING'
  | 'OPS' | 'EMERGENCY' | 'OWNER' | 'COMPLAINT' | 'MAINTENANCE' | 'OTHER'

export type CallResolutionStatus = 'resolved' | 'unresolved' | 'unclear' | 'not_applicable'

export interface AiPromptPreview {
  /** Tenant-authored prompt as stored server-side. */
  aiPrompt: string
  /** Machine-generated additions appended server-side. Read-only. */
  suffix: string
  /** Exactly what is sent to Vapi as the system message. */
  composed: string
}

// Every field here must correspond to a query param GET /calls/logs actually
// declares. dateFrom/dateTo used to be listed and were never implemented
// server-side — FastAPI ignores an undeclared param rather than rejecting it, so
// a field like that reads as "supported" while doing nothing. Removed rather
// than left as a trap; add it back the day the endpoint grows a date filter.
export interface CallLogListParams {
  /** Exact call status. An unknown value returns an empty page server-side. */
  status?: CallStatus
  page?: number
  /** Serialized as page_size — see getCallLogs. */
  pageSize?: number
  /** Case-insensitive substring over caller number / guest name / property. */
  search?: string
  /**
   * Only calls linked to this reservation, newest first. Unpaginated server-side
   * (a reservation has a handful of calls at most). Still tenant-scoped — the id is
   * a filter, not an access token.
   */
  reservationId?: SnowflakeId
}

export interface RoutingConfig {
  id: string
  name: string
  ringGroup: string
  ringTimeout: number
  isActive: boolean
}

export interface CreateRoutingConfigPayload {
  name: string
  ringGroup: string
  ringTimeout: number
  isActive: boolean
}

// ─── Call-routing settings (ring levels + rules) ─────────────────────────────────
// NOTE: the legacy `RoutingConfig` above (camelCase, used by call-routing.tsx) is a
// flat summary shape. The types below are the richer snake_case detail shape returned
// by the /calls/routing detail + rules endpoints, and back the call-routing settings
// page. They are deliberately distinct from `RoutingConfig` so the existing page keeps
// compiling. Field names mirror the backend payload verbatim (snake_case, not aliased).

// Mirrors main-api app/shared/constants/call_matching.py. Duplicated rather than
// fetched because these are the values shown in an empty/legacy field before any
// request completes; they must match the DB server_default (30 / 90).
export const GUEST_MATCH_DEFAULT_LOOKBACK = 30
export const GUEST_MATCH_DEFAULT_LOOKAHEAD = 90

export type RoutingConditionType = 'unknown_caller' | 'resort_match' | 'always'
export type RoutingActionType = 'forward' | 'voicemail' | 'use_levels'
// 'voicemail' is the TERMINAL level (Phase 2): it replaces the removed
// fallback_type/fallback_value scalars, must be the last level, at most one.
export type RoutingRingType = 'role' | 'users' | 'external' | 'voicemail'

export interface RoutingRule {
  id: string
  routing_config_id: string
  priority: number
  condition_type: RoutingConditionType
  condition_value: string | null
  action_type: RoutingActionType
  action_value: string | null
  is_active: boolean
}

// Same shape as RoutingRule, minus server-assigned id / routing_config_id.
export type RoutingRuleInput = Omit<RoutingRule, 'id' | 'routing_config_id'>

export interface RoutingLevel {
  id: string
  level: number
  role_id: string | null
  ring_timeout_seconds: number
  ring_type: RoutingRingType
  // Explicit user Snowflake IDs to ring when ring_type='users'; kept as strings
  // (never coerced to number; see CLAUDE.md). Empty/absent for other ring types.
  user_ids?: string[]
  external_phone: string | null
  // Group IDs are Snowflake IDs → kept as strings (never coerced to number; see CLAUDE.md).
  resort_filter: string[] | null
  // Voicemail prompt when ring_type='voicemail' (terminal level); null otherwise.
  voicemail_message: string | null
}

// Same shape as RoutingLevel, minus server-assigned id.
export type RoutingLevelInput = Omit<RoutingLevel, 'id'>

// ─── Ring groups ──────────────────────────────────────────────────────────────
// Independent ring groups replace per-level resort_filter: the resort scope
// lives on the GROUP, and levels are ordered only within their own group — so
// two resorts can have genuinely independent priority orderings instead of
// being interleaved into one shared list. When ring_groups is non-empty it
// supersedes the flat `levels` array for routing.

export interface RingGroupLevel {
  // Stable id, unique across the config. This is the persisted escalation
  // cursor — echo it back unchanged on save so in-flight ring-timeout jobs keep
  // resolving. Absent on a level the user just added (server mints one).
  id: number | null
  // 1-based order WITHIN this group. Derived from array order on save; the
  // server re-derives it too, so array order is the single source of truth.
  position: number
  role_id: string | null
  ring_type: RoutingRingType
  user_ids?: string[]
  external_phone: string | null
  ring_timeout_seconds: number
  voicemail_message: string | null
}

export interface RingGroup {
  // Stable slug, unique within the config; survives renames.
  key: string
  name: string
  // Group match order; lower wins. The is_default group always evaluates last.
  priority: number
  // Group IDs are Snowflake IDs → kept as strings (never coerce to number).
  resort_ids: string[]
  // The catch-all: used when the caller's resort matches no group, AND for
  // callers with no reservation (no resort at all).
  is_default: boolean
  levels: RingGroupLevel[]
}

export type RingGroupLevelInput = RingGroupLevel
export type RingGroupInput = RingGroup

// Phase 2 contract: fallback_type/fallback_value are GONE — the no-answer behavior
// is the levels[] terminal entry (ring_type='voicemail' with voicemail_message, or a
// trailing ring_type='external' forward). twilio_number was renamed phone_number.
export interface RoutingConfigDetail {
  id: string
  name: string
  is_active: boolean
  phone_number: string | null
  // Admin-facing free-text notes about this config.
  description?: string | null
  // Per-tenant pre-queue recording greeting, keyed by locale.
  recording_message_i18n?: Record<string, string>
  // Generic extension bag reserved for future tenant-level settings.
  extra_info?: Record<string, unknown>
  // How far around "now" a reservation still counts as an inbound caller's stay
  // when matching their phone number to a guest. Was a hardcoded 30/90 in
  // main-api's reservation_crud; per-number because the lines a tenant owns
  // (front desk vs owner line) get different callers. Optional here so a build
  // talking to a backend without the columns still type-checks — the UI falls
  // back to the same defaults the DB uses.
  guest_match_lookback_days?: number
  guest_match_lookahead_days?: number
  levels: RoutingLevel[]
  // Non-empty means this config routes by ring groups and `levels` is legacy.
  ring_groups: RingGroup[]
  rules: RoutingRule[]
}

// ─────────────────────────────────────────────────────────────────────────────
// AI Receptionist Settings (Vapi) — Phase 1+2 contract. NOT embedded in
// RoutingConfigDetail: a separate GET, same as getRules for RoutingRule[].
// ─────────────────────────────────────────────────────────────────────────────

export interface AiTakeoverSchedule {
  start_time: string
  end_time: string
  timezone: string
  // 0=Monday..6=Sunday; null = every day.
  days_of_week: number[] | null
}

// How long the assistant waits after a caller stops talking before it assumes
// they are finished. Exposed as three presets rather than raw milliseconds: the
// two Vapi fields behind it (transcriber.maxEndpointDelayMs and
// startSpeakingPlan.waitSeconds) have to move together, and nobody configuring a
// phone line should be asked to reason about either. Mirrors main-api's
// app/shared/constants/vapi_speech.py — ordered fastest → most patient.
export const PAUSE_TOLERANCE_OPTIONS = ['quick', 'normal', 'patient'] as const
export type PauseTolerance = (typeof PAUSE_TOLERANCE_OPTIONS)[number]
export const PAUSE_TOLERANCE_DEFAULT: PauseTolerance = 'normal'

/**
 * One selectable Vapi voice. Comes from the server with the settings — never
 * hardcoded here. Vapi documents no voice list at all (its OpenAPI spec
 * declares voiceId as a free-form string and never mentions "Elliot"), so the
 * catalogue was built by probing the live API, and Vapi retires voices: eight
 * are already rejected for new assistants. A frontend copy would go stale and
 * offer options that 400 on save.
 */
export interface VoiceOption {
  /** Exact value to send as ai_voice_id. Case-sensitive — Vapi rejects 'elliot'. */
  voice_id: string
  gender: string
  accent: string
  /** Short human description, e.g. 'Young, calm'. */
  character: string
  /** 'production' = Vapi-labelled Natural/Realistic. 'beta' = must be marked in the UI. */
  tier: 'production' | 'beta'
}

/** Fallback when a backend predating the voice picker sends no selection. */
export const DEFAULT_VOICE_ID = 'Elliot'

export interface AiSettings {
  ai_enabled: boolean
  ai_prompt: string | null
  /** Which built-in Vapi voice answers this number. Never null. */
  ai_voice_id: string
  /** Everything selectable, production tier first, each tagged. */
  voice_options: VoiceOption[]
  /**
   * The exact text to put in the single editable prompt box. When
   * ai_prompt_is_full is false this is the fully COMPOSED system message
   * (ai_prompt plus the machine-generated additions), so the box opens showing
   * what is actually live; when true it is ai_prompt verbatim. Never empty — a
   * config with no prompt yet gets a starting template. Bind the textarea to
   * THIS, not ai_prompt.
   */
  ai_prompt_full: string
  /**
   * False: ai_prompt is only the tenant-authored half and the server appends
   * the additions on every sync. True: the tenant has saved through the single
   * full-text box and owns the whole system message — nothing is appended.
   */
  ai_prompt_is_full: boolean
  // Greeting the assistant says when a call connects. Optional — the backend
  // falls back to a hardcoded default greeting when this is null/blank, so
  // (unlike ai_prompt) it's never required to enable AI.
  ai_first_message: string | null
  ai_takeover_schedule: AiTakeoverSchedule | null
  // Never null on the way out: the column is NOT NULL with a server default and
  // the backend normalizes anything unrecognised, so the selector always has an
  // option to show.
  ai_pause_tolerance: PauseTolerance
  // Backend-managed, set after a successful Vapi Assistant sync. Read-only.
  vapi_assistant_id: string | null
}

// PUT body: every field optional (server is exclude_unset partial-update, same
// convention as updateRoutingConfig).
export interface AiSettingsInput {
  ai_enabled?: boolean
  ai_prompt?: string | null
  /** Omit to leave the stored voice untouched — sending it re-pushes the assistant. */
  ai_voice_id?: string
  /**
   * Send true together with an ai_prompt that is the COMPLETE system message
   * (what the single prompt box holds). The server then stores it verbatim and
   * appends nothing on any later sync.
   */
  ai_prompt_is_full?: boolean
  ai_first_message?: string | null
  ai_takeover_schedule?: AiTakeoverSchedule | null
  ai_pause_tolerance?: PauseTolerance
}

// A purchasable phone number returned by the availability search
// (GET /calls/phone-numbers/available).
export interface AvailableNumberResult {
  phoneNumber: string
  friendlyName: string
  locality: string | null
  region: string | null
}

function proxyRecordingUrl(twilioUrl: string | null): string | null {
  if (!twilioUrl) return null
  const match = twilioUrl.match(/\/(RE[a-f0-9]+)/)
  if (!match) return twilioUrl
  return `${API_BASE}/calls/recording/${match[1]}`
}

// A browser <audio> element can't send an Authorization header, so the recording
// proxy (GET /calls/recording/{id}) also accepts the JWT as ?token=. Resolve the
// same token axios sends (Clerk first, legacy localStorage token as fallback).
async function getMediaAuthToken(): Promise<string | null> {
  try {
    if (window.__getClerkToken) {
      const clerkToken = await window.__getClerkToken()
      if (clerkToken) return clerkToken
    }
  } catch {
    // fall through to the legacy localStorage token
  }
  return localStorage.getItem('ACCESS_TOKEN')
}

// Backend returns camelCase via Pydantic alias_generator, but two field names differ:
//   answeredByUserId  → answeredBy   (backend: answered_by_user_id)
//   durationSeconds   → duration     (backend: duration_seconds)
function mapCallLog(raw: Record<string, unknown>): CallLog {
  const mappedUrl = proxyRecordingUrl((raw.recordingUrl ?? null) as string | null)
  return {
    id: raw.id as string,
    callSid: raw.callSid as string,
    callerNumber: raw.callerNumber as string,
    guestName: (raw.guestName ?? null) as string | null,
    listingName: (raw.listingName ?? null) as string | null,
    reservationId: (raw.reservationId ?? null) as SnowflakeId | null,
    otaConfirmationCode: (raw.otaConfirmationCode ?? null) as string | null,
    checkIn: (raw.checkIn ?? null) as string | null,
    checkOut: (raw.checkOut ?? null) as string | null,
    answeredBy: (raw.answeredByUserId ?? null) as string | null,
    answeredByName: (raw.answeredByName ?? null) as string | null,
    duration: (raw.durationSeconds ?? null) as number | null,
    status: raw.status as CallStatus,
    dispatchType: (raw.dispatchType ?? null) as CallDispatchType | null,
    forwardToNumber: (raw.forwardToNumber ?? null) as string | null,
    isResolved: (raw.isResolved ?? false) as boolean,
    createdAt: raw.createdAt as string,
    aiSummary: (raw.aiSummary ?? null) as string | null,
    transcript: (raw.transcript ?? null) as string | null,
    recordingUrl: mappedUrl,
    onHold: (raw.onHold ?? false) as boolean,
    professionalismScore: (raw.professionalismScore ?? null) as number | null,
    professionalismNote: (raw.professionalismNote ?? null) as string | null,
    resolutionStatus: (raw.resolutionStatus ?? null) as CallResolutionStatus | null,
    // ?? [] guards a server that predates the topics field — the UI maps over this
    // unconditionally, so undefined here would be a render crash, not a missing chip.
    topics: (raw.topics ?? []) as CallTopicCode[],
  }
}

// The routing endpoints serialize responses in camelCase (Pydantic alias_generator),
// but the settings page (and PUT body) work in snake_case. Remap on the way in, same
// as mapCallLog above. camelCase is primary; snake_case fallbacks guard against an
// environment that already returns snake_case.
function mapRoutingLevel(raw: Record<string, unknown>): RoutingLevel {
  return {
    id: (raw.id ?? '') as string,
    level: (raw.level ?? 0) as number,
    role_id: (raw.roleId ?? raw.role_id ?? null) as string | null,
    ring_timeout_seconds: (raw.ringTimeoutSeconds ?? raw.ring_timeout_seconds ?? 0) as number,
    ring_type: (raw.ringType ?? raw.ring_type) as RoutingRingType,
    user_ids: (raw.userIds ?? raw.user_ids ?? []) as string[],
    external_phone: (raw.externalPhone ?? raw.external_phone ?? null) as string | null,
    resort_filter: (raw.resortFilter ?? raw.resort_filter ?? null) as string[] | null,
    voicemail_message: (raw.voicemailMessage ?? raw.voicemail_message ?? null) as string | null,
  }
}

function mapRingGroupLevel(raw: Record<string, unknown>): RingGroupLevel {
  return {
    id: (raw.id ?? null) as number | null,
    position: (raw.position ?? 0) as number,
    role_id: (raw.roleId ?? raw.role_id ?? null) as string | null,
    ring_type: (raw.ringType ?? raw.ring_type) as RoutingRingType,
    user_ids: (raw.userIds ?? raw.user_ids ?? []) as string[],
    external_phone: (raw.externalPhone ?? raw.external_phone ?? null) as string | null,
    ring_timeout_seconds: (raw.ringTimeoutSeconds ?? raw.ring_timeout_seconds ?? 20) as number,
    voicemail_message: (raw.voicemailMessage ?? raw.voicemail_message ?? null) as string | null,
  }
}

function mapRingGroup(raw: Record<string, unknown>): RingGroup {
  const levels = (raw.levels ?? []) as Record<string, unknown>[]
  return {
    key: (raw.key ?? '') as string,
    name: (raw.name ?? '') as string,
    priority: (raw.priority ?? 1) as number,
    // Snowflake group ids arrive as strings and stay strings.
    resort_ids: (raw.resortIds ?? raw.resort_ids ?? []) as string[],
    is_default: (raw.isDefault ?? raw.is_default ?? false) as boolean,
    levels: levels.map(mapRingGroupLevel),
  }
}

function mapRoutingRule(raw: Record<string, unknown>): RoutingRule {
  return {
    id: (raw.id ?? '') as string,
    routing_config_id: (raw.routingConfigId ?? raw.routing_config_id ?? '') as string,
    priority: (raw.priority ?? 0) as number,
    condition_type: (raw.conditionType ?? raw.condition_type) as RoutingConditionType,
    condition_value: (raw.conditionValue ?? raw.condition_value ?? null) as string | null,
    action_type: (raw.actionType ?? raw.action_type) as RoutingActionType,
    action_value: (raw.actionValue ?? raw.action_value ?? null) as string | null,
    is_active: (raw.isActive ?? raw.is_active ?? false) as boolean,
  }
}

function mapRoutingConfigDetail(raw: Record<string, unknown>): RoutingConfigDetail {
  const levels = (raw.levels ?? []) as Record<string, unknown>[]
  const ringGroups = (raw.ringGroups ?? raw.ring_groups ?? []) as Record<string, unknown>[]
  const rules = (raw.rules ?? []) as Record<string, unknown>[]
  return {
    id: (raw.id ?? '') as string,
    name: (raw.name ?? '') as string,
    is_active: (raw.isActive ?? raw.is_active ?? false) as boolean,
    // twilio_number fallbacks guard against a not-yet-migrated backend build.
    phone_number: (raw.phoneNumber ?? raw.phone_number
      ?? raw.twilioNumber ?? raw.twilio_number ?? null) as string | null,
    description: (raw.description ?? null) as string | null,
    recording_message_i18n: (raw.recordingMessageI18n ?? raw.recording_message_i18n
      ?? {}) as Record<string, string>,
    extra_info: (raw.extraInfo ?? raw.extra_info ?? {}) as Record<string, unknown>,
    // Defaults mirror main-api's DEFAULT_GUEST_MATCH_* (and the DB server_default),
    // so a response from a backend that predates the columns shows the window
    // that is genuinely in effect rather than an empty field.
    guest_match_lookback_days: (raw.guestMatchLookbackDays ?? raw.guest_match_lookback_days
      ?? GUEST_MATCH_DEFAULT_LOOKBACK) as number,
    guest_match_lookahead_days: (raw.guestMatchLookaheadDays ?? raw.guest_match_lookahead_days
      ?? GUEST_MATCH_DEFAULT_LOOKAHEAD) as number,
    levels: levels.map(mapRoutingLevel),
    ring_groups: ringGroups.map(mapRingGroup),
    rules: rules.map(mapRoutingRule),
  }
}

function mapAiTakeoverSchedule(raw: Record<string, unknown>): AiTakeoverSchedule {
  return {
    start_time: (raw.startTime ?? raw.start_time ?? '') as string,
    end_time: (raw.endTime ?? raw.end_time ?? '') as string,
    timezone: (raw.timezone ?? '') as string,
    days_of_week: (raw.daysOfWeek ?? raw.days_of_week ?? null) as number[] | null,
  }
}

function mapAiSettings(raw: Record<string, unknown>): AiSettings {
  const rawSchedule = (raw.aiTakeoverSchedule ?? raw.ai_takeover_schedule ?? null) as
    | Record<string, unknown>
    | null
  return {
    ai_enabled: (raw.aiEnabled ?? raw.ai_enabled ?? false) as boolean,
    ai_prompt: (raw.aiPrompt ?? raw.ai_prompt ?? null) as string | null,
    // Falls back to Elliot rather than '' : a backend deployed before this
    // column existed sends nothing, and an empty value would render the picker
    // with no option chosen — making the first save look like a deliberate
    // voice change the user never made. Elliot is what those configs are
    // actually on (it was the hardcoded constant), so this is the true value.
    ai_voice_id: (raw.aiVoiceId ?? raw.ai_voice_id ?? DEFAULT_VOICE_ID) as string,
    voice_options: mapVoiceOptions(raw.voiceOptions ?? raw.voice_options),
    // Falls back to ai_prompt, not to '': against a backend deployed before this
    // field existed the box must still open on the tenant's text rather than
    // blank — an empty box here reads as "the prompt was deleted", and saving it
    // would then actually delete it.
    ai_prompt_full: (raw.aiPromptFull ?? raw.ai_prompt_full
      ?? raw.aiPrompt ?? raw.ai_prompt ?? '') as string,
    ai_prompt_is_full: (raw.aiPromptIsFull ?? raw.ai_prompt_is_full ?? false) as boolean,
    ai_first_message: (raw.aiFirstMessage ?? raw.ai_first_message ?? null) as string | null,
    ai_takeover_schedule: rawSchedule ? mapAiTakeoverSchedule(rawSchedule) : null,
    // Narrowed against the known options rather than cast: a backend that
    // predates the column sends nothing, and an unrecognised value must land on
    // the same default the backend itself resolves to — otherwise the selector
    // would render with nothing selected and the first save would look like a
    // deliberate change the user never made.
    ai_pause_tolerance: toPauseTolerance(raw.aiPauseTolerance ?? raw.ai_pause_tolerance),
    vapi_assistant_id: (raw.vapiAssistantId ?? raw.vapi_assistant_id ?? null) as string | null,
  }
}

function mapVoiceOptions(raw: unknown): VoiceOption[] {
  if (!Array.isArray(raw)) return []
  return raw.map((o): VoiceOption => {
    const v = o as Record<string, unknown>
    return {
      voice_id: (v.voiceId ?? v.voice_id ?? '') as string,
      gender: (v.gender ?? '') as string,
      accent: (v.accent ?? '') as string,
      character: (v.character ?? '') as string,
      // Anything not explicitly 'beta' is treated as production. Erring the
      // other way would tag the whole list beta on a field-name mismatch and
      // make every voice look risky.
      tier: v.tier === 'beta' ? 'beta' : 'production',
    }
  }).filter((o) => o.voice_id)
}

export function toPauseTolerance(value: unknown): PauseTolerance {
  return PAUSE_TOLERANCE_OPTIONS.includes(value as PauseTolerance)
    ? (value as PauseTolerance)
    : PAUSE_TOLERANCE_DEFAULT
}

function mapAvailableNumber(raw: Record<string, unknown>): AvailableNumberResult {
  return {
    phoneNumber: (raw.phoneNumber ?? raw.phone_number ?? '') as string,
    friendlyName: (raw.friendlyName ?? raw.friendly_name ?? '') as string,
    locality: (raw.locality ?? null) as string | null,
    region: (raw.region ?? null) as string | null,
  }
}

export const callApi = {
  getToken: async (signal?: AbortSignal): Promise<TwilioTokenResponse> => {
    const raw = await axios.post<unknown, { token: string; identity: string; ttl: number }>('/calls/token', undefined, { signal, timeout: 15_000 })
    return { token: raw.token, expiresAt: new Date(Date.now() + raw.ttl * 1000).toISOString() }
  },

  answerCall: (callSid: string) =>
    axios.post<unknown, ActiveCall>('/calls/answer', { callSid }),

  endCall: (callSid: string) =>
    axios.post<unknown, void>('/calls/end', { callSid }),

  // Decline routes the caller to voicemail server-side and marks the call missed;
  // it also broadcasts a missed status so other agents' popups dismiss.
  declineCall: (callSid: string) =>
    axios.post<unknown, { success: boolean; callSid: string }>('/calls/decline', { callSid }),

  holdCall: (callSid: string) =>
    axios.post<unknown, { success: boolean; callSid: string }>('/calls/hold', { callSid }),

  unholdCall: (callSid: string) =>
    axios.post<unknown, { success: boolean; callSid: string }>('/calls/unhold', { callSid }),

  getCallLogs: async (params?: CallLogListParams): Promise<{ logs: CallLog[]; total: number }> => {
    // EVERY multi-word param is remapped to the snake_case name the endpoint
    // actually declares. There is no camelCase→snake_case param serializer in
    // axios-config.ts, and FastAPI does not reject an unknown query param — it
    // ignores it and uses the declared default. So a camelCase name here does not
    // fail loudly, it fails silently:
    //   · pageSize      → server used its own default of 20 (which happened to
    //                     equal the client's PAGE_SIZE, hiding the bug entirely)
    //   · reservationId → every call in the tenant instead of this reservation's
    // Single-word params (status, search, page) are already snake_case-identical
    // and pass through untouched.
    const { reservationId, pageSize, ...rest } = params ?? {}
    const query: Record<string, unknown> = { ...rest }
    if (reservationId) query.reservation_id = reservationId
    if (pageSize !== undefined) query.page_size = pageSize
    const raw = await axios.get<unknown, { items: Record<string, unknown>[]; total: number }>('/calls/logs', { params: query })
    return { logs: raw.items.map(mapCallLog), total: raw.total }
  },

  getCallLog: (id: string): Promise<CallLog> =>
    axios.get<unknown, Record<string, unknown>>(`/calls/logs/${id}`).then(mapCallLog),

  // Mark a voicemail resolved; clears it from the unread voicemail count.
  resolveVoicemail: (id: string) =>
    axios.post<unknown, { success: boolean }>(`/calls/logs/${id}/resolve`),

  // Sidebar badge: count of voicemail-status call logs for the tenant.
  getVoicemailCount: async (): Promise<number> => {
    const data = await axios.get<unknown, { count?: number }>('/calls/voicemail-count')
    return (data as { count?: number } | null)?.count ?? 0
  },

  // ─── Active calls (persistent call-status icon) ────────────────────────────
  // Seeds the shared active-calls store on page load, before any WS event.
  getActiveCalls: async (signal?: AbortSignal): Promise<{ calls: CallLog[]; count: number; incoming?: IncomingCall[] }> => {
    const raw = await axios.get<unknown, { items: Record<string, unknown>[]; count: number; incoming?: Record<string, unknown>[] | null }>('/calls/active', { signal })
    return { calls: raw.items.map(mapCallLog), count: raw.count, incoming: raw.incoming?.map(mapIncomingCall) }
  },

  // ─── Do Not Disturb ─────────────────────────────────────────────────────────
  getDnd: async (): Promise<boolean> => {
    const raw = await axios.get<unknown, { dnd: boolean }>('/staff/dnd')
    return raw.dnd
  },

  setDnd: async (dnd: boolean): Promise<boolean> => {
    const raw = await axios.patch<unknown, { dnd: boolean }>('/staff/dnd', { dnd })
    return raw.dnd
  },

  // Build a playable recording URL with the JWT appended as ?token= so a browser
  // <audio src> can authenticate without an Authorization header. Returns null when
  // there is no recording. The token is the same one axios uses for API calls.
  recordingPlaybackUrl: async (recordingUrl: string | null | undefined): Promise<string | null> => {
    if (!recordingUrl) return null
    const token = await getMediaAuthToken()
    if (!token) return recordingUrl
    const sep = recordingUrl.includes('?') ? '&' : '?'
    return `${recordingUrl}${sep}token=${encodeURIComponent(token)}`
  },

  getRoutingConfigs: () =>
    axios.get<unknown, { configs: RoutingConfig[] }>('/calls/routing'),

  // Settings page creates a config from the empty state with a partial snake_case
  // body (name + terminal voicemail level); the backend fills the rest with defaults.
  createRoutingConfig: (data: {
    name: string
    is_active?: boolean
    phone_number?: string | null
    levels?: RoutingLevelInput[]
    rules?: RoutingRuleInput[]
  }) => axios.post<unknown, RoutingConfigDetail>('/calls/routing', data),

  // PUT semantics are exclude_unset server-side: only the fields present in the
  // body are written, everything omitted is left untouched (so a levels-only save
  // can't wipe phone_number etc). Unknown fields are rejected (extra='forbid').
  updateRoutingConfig: (
    id: string,
    data: Partial<CreateRoutingConfigPayload> | Record<string, unknown>,
  ) => axios.put<unknown, RoutingConfig>(`/calls/routing/${id}`, data),

  // ─── Settings page: detail config (with nested levels + rules) ──────────────
  // GET /calls/routing returns the list of configs; the settings page edits the
  // single active config, so unwrap to one detail object (or null when none exist).
  // With a configId, returns that specific config (or null if it's gone); without
  // one, falls back to the first/active config. There is no GET /calls/routing/{id}
  // single-config endpoint, so we select from the list either way.
  getRoutingConfig: async (configId?: string): Promise<RoutingConfigDetail | null> => {
    const raw = await axios.get<unknown, unknown>('/calls/routing')
    const list = (Array.isArray(raw)
      ? raw
      : ((raw as { configs?: unknown[] } | null)?.configs ?? [])) as Record<string, unknown>[]
    const configs = list.map(mapRoutingConfigDetail)
    if (configId) return configs.find((c) => c.id === configId) ?? null
    return configs[0] ?? null
  },

  getRules: async (configId: string): Promise<RoutingRule[]> => {
    const raw = await axios.get<unknown, { rules?: RoutingRule[] } | RoutingRule[]>(`/calls/routing/${configId}/rules`)
    return Array.isArray(raw) ? raw : (raw?.rules ?? [])
  },

  replaceRules: (configId: string, rules: RoutingRuleInput[]) =>
    axios.put<unknown, RoutingRule[]>(`/calls/routing/${configId}/rules`, rules),

  // ─── AI Receptionist Settings (Vapi) ────────────────────────────────────────
  getAiSettings: (configId: string): Promise<AiSettings> =>
    axios.get<unknown, Record<string, unknown>>(`/calls/routing/${configId}/ai-settings`).then(mapAiSettings),

  // Read-only preview of the system message actually sent to Vapi. The suffix is
  // fetched, never hardcoded here: a frontend copy would silently drift from
  // vapi_client.compose_system_prompt and recreate the exact confusion the
  // preview exists to end (2026-08-10: 88 authored chars vs 2076 sent).
  getAiPromptPreview: (configId: string): Promise<AiPromptPreview> =>
    axios
      .get<unknown, Record<string, unknown>>(`/calls/routing/${configId}/ai-settings/prompt-preview`)
      .then((raw) => ({
        aiPrompt: (raw.aiPrompt ?? raw.ai_prompt ?? '') as string,
        suffix: (raw.suffix ?? '') as string,
        composed: (raw.composed ?? '') as string,
      })),

  // Same PUT convention as updateRoutingConfig: exclude_unset server-side, so
  // only the fields present in `data` are written.
  updateAiSettings: (configId: string, data: AiSettingsInput): Promise<AiSettings> =>
    axios
      .put<unknown, Record<string, unknown>>(`/calls/routing/${configId}/ai-settings`, data)
      .then(mapAiSettings),

  // ─── Phone-number management ────────────────────────────────────────────────
  // GET /calls/routing returns the full detail shape (with phone_number); unlike
  // getRoutingConfigs (flat legacy summary) this maps every config to the detail
  // shape the phone-numbers page needs.
  getRoutingConfigDetails: async (): Promise<RoutingConfigDetail[]> => {
    const raw = await axios.get<unknown, unknown>('/calls/routing')
    const list = (Array.isArray(raw)
      ? raw
      : ((raw as { configs?: unknown[] } | null)?.configs ?? [])) as Record<string, unknown>[]
    return list.map(mapRoutingConfigDetail)
  },

  // Search the vendor's inventory for purchasable numbers.
  getAvailableNumbers: async (
    params: { area_code?: string; country?: string; limit?: number },
  ): Promise<AvailableNumberResult[]> => {
    const raw = await axios.get<unknown, unknown>('/calls/phone-numbers/available', { params })
    const list = (Array.isArray(raw) ? raw : []) as Record<string, unknown>[]
    return list.map(mapAvailableNumber)
  },

  // Buy a number, wire its inbound webhook, and create a routing config for it.
  provisionNumber: (body: { phone_number: string; name: string; description?: string }): Promise<RoutingConfigDetail> =>
    axios
      .post<unknown, Record<string, unknown>>('/calls/phone-numbers/provision', body)
      .then(mapRoutingConfigDetail),

  // Release the config's provisioned number and delete the config (204 No Content).
  releaseNumber: (configId: string): Promise<void> =>
    axios.delete<unknown, void>(`/calls/routing/${configId}/release`),
}
